import { Grid, IconButton, InputAdornment } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PriceModifierAction } from 'src/common/enumerations/price-modifier-action';
import { PriceOverrideType } from 'src/common/enumerations/price-override-type';
import { rem } from 'src/common/helpers/dimension-helper';
import { themeColor } from 'src/mui-theme';
import { CssTextField, styles } from './price-override-input-styles';

const PriceOverrideInput = ({
  input,
  action,
  focusedAction,
  unit,
  priceOverrideType,
  onChange,
  onClear,
  onFocus,
  onKeyDown,
  inputRef,
}: IPriceOverrideInputProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  const isFocused = action === focusedAction;
  const disabled =
    priceOverrideType === PriceOverrideType.Regular && action !== PriceModifierAction.NewPrice;

  const inputName = (action: PriceModifierAction): string => {
    switch (action) {
      case PriceModifierAction.Amount:
        return t('Discount');
      case PriceModifierAction.Percent:
        return t('PriceOverrideScreen_Percentdiscount');
      default:
        return t('GetItemForPriceOverrideView_Price');
    }
  };

  return (
    <Grid container className={classes.priceInput}>
      <Grid item xs={5}>
        <span
          data-testid={`price-override-${action}-label`}
          className={`${classes.subHeading} ${isFocused ? '' : classes.halfOpacity}`}
        >
          {inputName(action)}
        </span>
      </Grid>
      <Grid item xs={7}>
        <Grid container>
          <Grid item>
            {action === PriceModifierAction.NewPrice || action === PriceModifierAction.Amount ? (
              <CssTextField
                disabled={disabled}
                className={`${classes.textFieldInput} ${isFocused ? '' : classes.greyUnderline}`}
                variant='outlined'
                type='string'
                id={action}
                name={action}
                autoComplete='off'
                onFocus={onFocus}
                inputProps={{
                  'data-testid': `price-override-${action.toLowerCase()}-input`,
                  'data-keyboard-owner': `${action}-focus`,
                }}
                InputProps={{
                  endAdornment: (
                    <InputCancelAdornment
                      name={action}
                      input={isFocused ? input : ''}
                      cancelIconSize='small'
                      clearInput={onClear}
                    />
                  ),
                }}
                value={isFocused ? input : ''}
                autoFocus={action === PriceModifierAction.NewPrice}
                onChange={onChange}
                onKeyDown={onKeyDown}
                inputRef={inputRef}
              />
            ) : (
              <CssTextField
                disabled={disabled}
                className={`${classes.textFieldInput} ${isFocused ? '' : classes.greyUnderline}`}
                variant='outlined'
                type='string'
                id={action}
                name={action}
                autoComplete='off'
                onFocus={onFocus}
                inputProps={{
                  'data-testid': `price-override-${action.toLowerCase()}-input`,
                  'data-keyboard-owner': `${action}-focus`,
                }}
                InputProps={{
                  endAdornment: (
                    <InputCancelAdornment
                      name={action}
                      input={isFocused ? input : ''}
                      cancelIconSize='small'
                      clearInput={onClear}
                    />
                  ),
                }}
                value={isFocused ? input : ''}
                onChange={onChange}
                onKeyDown={onKeyDown}
              />
            )}
          </Grid>
          <Grid xs={1} />
          <Grid item className={classes.priceUnit}>
            <span className={`${classes.HeadlineCaption} ${isFocused ? '' : classes.halfOpacity}`}>
              / {unit}
            </span>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface IInputCancelAdornmentProps {
  name: string;
  input: string;
  cancelIconSize?: 'small' | 'large';
  clearInput: () => void;
}

const InputCancelAdornment = ({
  name,
  input,
  cancelIconSize = 'large',
  clearInput,
}: IInputCancelAdornmentProps): JSX.Element => {
  return (
    <InputAdornment position='end'>
      <IconButton
        style={{ padding: cancelIconSize === 'small' ? 0 : 12 }}
        data-testid={`clear-button-${name}`}
        aria-label={`clear ${name} input`}
        onClick={(e): void => {
          clearInput();
          e.preventDefault();
        }}
      >
        {input ? (
          <ClearIcon
            style={{ fontSize: cancelIconSize === 'small' ? rem(1) : rem(1.5) }}
            htmlColor={`${themeColor.infoMain}`}
          />
        ) : (
          ''
        )}
      </IconButton>
    </InputAdornment>
  );
};

export interface IPriceOverrideInputProps {
  key: string;
  input: string;
  action: PriceModifierAction;
  focusedAction: PriceModifierAction;
  unit: string;
  priceOverrideType: string;
  onChange: (e) => void;
  onFocus: (e) => void;
  onClear: () => void;
  onKeyDown: (e) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

export default PriceOverrideInput;
